import React from 'react';
import { Offcanvas, OffcanvasHeader, OffcanvasBody, Form, FormGroup, Label, Input, Button, FormFeedback, Spinner } from 'reactstrap';
import AsyncSelect from 'react-select/async';
import { useCreateTask } from '../hooks/useCreateTask';
import { TASK_TYPE_OPTIONS, TASK_PRIORITY_OPTIONS } from '../../../../constants/tasks';

const CreateTaskSidebar = ({ isOpen, toggle, onTaskCreated }) => {

    const { formik, members, employees, loadingResources } = useCreateTask(onTaskCreated, toggle);

    // Mapeamento e Debounce Helper
    const memberOptions = members.map(m => ({ value: m.id, label: m.fullName }));
    const employeeOptions = employees.map(e => ({ value: e.id, label: e.fullName || e.name }));

    const loadMemberOptions = (inputValue, callback) => {
        setTimeout(() => {
            const filtered = memberOptions.filter(i =>
                i.label.toLowerCase().includes(inputValue.toLowerCase())
            );
            callback(filtered);
        }, 600); // 400ms debounce timeout
    };

    const loadEmployeeOptions = (inputValue, callback) => {
        setTimeout(() => {
            const filtered = employeeOptions.filter(i =>
                i.label.toLowerCase().includes(inputValue.toLowerCase())
            );
            callback(filtered);
        }, 600);
    };

    return (
        <Offcanvas isOpen={isOpen} toggle={toggle} direction="end" style={{ width: "450px" }}>
            <OffcanvasHeader toggle={toggle} className="border-bottom pb-3 bg-light-subtle">
                <span className="fw-bold text-dark font-size-16">Criar Nova Tarefa</span>
            </OffcanvasHeader>
            <OffcanvasBody className="p-4 bg-white d-flex flex-column h-100 overflow-visible">
                <Form className="flex-grow-1" onSubmit={formik.handleSubmit}>
                    <FormGroup className="mb-4">
                        <Label className="fw-semibold text-muted font-size-13">Título da Tarefa <span className="text-danger">*</span></Label>
                        <Input
                            type="text"
                            name="title"
                            placeholder="Ex: Ligar para novo lead"
                            className="shadow-sm border-light"
                            value={formik.values.title}
                            onChange={formik.handleChange}
                            invalid={formik.touched.title && !!formik.errors.title}
                        />
                        <FormFeedback>{formik.errors.title}</FormFeedback>
                    </FormGroup>

                    <FormGroup className="mb-4">
                        <Label className="fw-semibold text-muted font-size-13">Descrição</Label>
                        <Input
                            type="textarea"
                            name="description"
                            rows="4"
                            placeholder="Detalhes da pendência..."
                            className="shadow-sm border-light"
                            value={formik.values.description}
                            onChange={formik.handleChange}
                        />
                    </FormGroup>

                    <div className="row">
                        <div className="col-12 col-md-6">
                            <FormGroup className="mb-4">
                                <Label className="fw-semibold text-muted font-size-13">Data <span className="text-danger">*</span></Label>
                                <Input
                                    type="date"
                                    name="scheduledDate"
                                    className="shadow-sm border-light"
                                    value={formik.values.scheduledDate}
                                    onChange={formik.handleChange}
                                    invalid={formik.touched.scheduledDate && !!formik.errors.scheduledDate}
                                />
                                <FormFeedback>{formik.errors.scheduledDate}</FormFeedback>
                            </FormGroup>
                        </div>
                        <div className="col-12 col-md-6">
                            <FormGroup className="mb-4">
                                <Label className="fw-semibold text-muted font-size-13">Hora</Label>
                                <Input
                                    type="time"
                                    name="scheduledTime"
                                    className="shadow-sm border-light"
                                    value={formik.values.scheduledTime}
                                    onChange={formik.handleChange}
                                />
                            </FormGroup>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12 col-md-6">
                            <FormGroup className="mb-4">
                                <Label className="fw-semibold text-muted font-size-13">Tipo <span className="text-danger">*</span></Label>
                                <Input
                                    type="select"
                                    name="type"
                                    className="shadow-sm border-light"
                                    value={formik.values.type}
                                    onChange={formik.handleChange}
                                    invalid={formik.touched.type && !!formik.errors.type}
                                >
                                    {TASK_TYPE_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </Input>
                                <FormFeedback>{formik.errors.type}</FormFeedback>
                            </FormGroup>
                        </div>
                        <div className="col-12 col-md-6">
                            <FormGroup className="mb-4">
                                <Label className="fw-semibold text-muted font-size-13">Prioridade <span className="text-danger">*</span></Label>
                                <Input
                                    type="select"
                                    name="priority"
                                    className="shadow-sm border-light"
                                    value={formik.values.priority}
                                    onChange={formik.handleChange}
                                    invalid={formik.touched.priority && !!formik.errors.priority}
                                >
                                    {TASK_PRIORITY_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </Input>
                                <FormFeedback>{formik.errors.priority}</FormFeedback>
                            </FormGroup>
                        </div>
                    </div>

                    <FormGroup className="mb-4">
                        <Label className="fw-semibold text-muted font-size-13 d-flex align-items-center">
                            Cliente Relacionado
                            {loadingResources && <Spinner size="sm" color="primary" className="ms-2" />}
                        </Label>
                        <AsyncSelect
                            cacheOptions
                            defaultOptions={memberOptions}
                            loadOptions={loadMemberOptions}
                            placeholder="Digite o nome..."
                            noOptionsMessage={() => "Nenhum cliente encontrado"}
                            loadingMessage={() => "Carregando..."}
                            isClearable
                            classNamePrefix="select"
                            value={memberOptions.find(o => o.value === formik.values.selectedMember) || null}
                            onChange={option => formik.setFieldValue('selectedMember', option ? option.value : '')}
                            isDisabled={loadingResources}
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    borderColor: '#e2e5e8',
                                    boxShadow: '0 .125rem .25rem rgba(0,0,0,.075)'
                                })
                            }}
                        />
                    </FormGroup>

                    <FormGroup className="mb-4">
                        <Label className="fw-semibold text-muted font-size-13 d-flex align-items-center">
                            Colaborador Relacionado
                            {loadingResources && <Spinner size="sm" color="primary" className="ms-2" />}
                        </Label>
                        <AsyncSelect
                            isMulti
                            cacheOptions
                            defaultOptions={employeeOptions}
                            loadOptions={loadEmployeeOptions}
                            placeholder="Selecione um ou mais..."
                            noOptionsMessage={() => "Nenhum colaborador encontrado"}
                            loadingMessage={() => "Carregando..."}
                            isClearable
                            classNamePrefix="select"
                            value={employeeOptions.filter(o => (formik.values.selectedEmployees || []).includes(o.value))}
                            onChange={options => formik.setFieldValue('selectedEmployees', options ? options.map(o => o.value) : [])}
                            isDisabled={loadingResources}
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    borderColor: '#e2e5e8',
                                    boxShadow: '0 .125rem .25rem rgba(0,0,0,.075)'
                                })
                            }}
                        />
                    </FormGroup>

                </Form>

                <div className="mt-auto border-top pt-4 pb-2">
                    <Button
                        color="primary"
                        className="w-100 py-3 fw-bold shadow rounded-3 font-size-15 transition-all hover-scale"
                        onClick={formik.submitForm}
                        disabled={formik.isSubmitting}
                    >
                        {formik.isSubmitting ? (
                            <><Spinner size="sm" className="me-2" /> Salvando...</>
                        ) : (
                            <>Salvar</>
                        )}
                    </Button>
                    <Button color="light" onClick={toggle} className="w-100 mt-2 py-2 fw-semibold rounded-3 font-size-14 text-muted" disabled={formik.isSubmitting}>
                        Cancelar
                    </Button>
                </div>
            </OffcanvasBody>
        </Offcanvas>
    );
};

export default CreateTaskSidebar;
